/**
 * demoState.js — the global Demo-mode flag.
 *
 * Demo mode ("Bemutató mód") is a GDPR-safe presentation switch: while it is on,
 * every piece of personally-identifiable data (names, e-mails, phones, addresses,
 * tax / bank / company numbers) that flows into the UI is transparently replaced
 * with deterministic synthetic values by {@link module:app/demo/demoRedactor}.
 * Real data keeps flowing and the app stays fully functional — only the *display*
 * is anonymised — so an operator can show the product to a prospect without
 * leaking a single real customer.
 *
 * The flag lives here as a synchronous in-memory mirror (`demoOn`) so that the
 * hot-path `isDemoMode()` is a plain boolean read and can be called from the
 * non-React data modules (BackgroundTasks, OrdersContext) that a Context/Redux
 * value could never reach.
 *
 * Demo mode is deliberately SESSION-ONLY: it is never persisted, so it lasts
 * exactly one app session and is OFF again on the next cold start, after logout,
 * and after a reinstall. `hydrateDemoMode()` (called once at boot from `App.jsx`)
 * also purges any value left behind by older builds that DID persist it — those
 * were written to both AsyncStorage and the SecureStore/Keychain twin, and the
 * Keychain copy survives even an uninstall, so it must be actively removed.
 *
 * A tiny local subscriber set powers reactive UI (the DEMO badge, the settings
 * switch, the POS order re-fetch) without depending on the single-handler
 * semantics of the global Observables bus.
 */

import {removeSetting} from '../helpers/persistentSettings';

/** persistentSettings key. */
export const DEMO_MODE_STORAGE_KEY = 'demoMode';

/** Synchronous in-memory mirror — the source of truth for `isDemoMode()`. */
let demoOn = false;

/** @type {Set<(enabled: boolean) => void>} */
const listeners = new Set();

/**
 * Cheap synchronous check — safe to call on every response / render.
 * @returns {boolean} whether demo mode is currently active.
 */
export function isDemoMode() {
    return demoOn === true;
}

/**
 * Notify every subscriber of the current flag value. Never lets one bad
 * listener break the others.
 * @returns {void}
 */
function notify() {
    listeners.forEach((fn) => {
        try {
            fn(demoOn);
        } catch (_) {
            // a broken subscriber must never break the toggle
        }
    });
}

/**
 * Subscribe to demo-mode changes (for the badge / settings switch / order refetch).
 * @param {(enabled: boolean) => void} fn callback invoked with the new value.
 * @returns {() => void} unsubscribe function.
 */
export function subscribeDemoMode(fn) {
    if (typeof fn !== 'function') {
        return () => {};
    }
    listeners.add(fn);

    return () => {
        listeners.delete(fn);
    };
}

/**
 * Turn demo mode on/off (in memory only — session-scoped, never persisted),
 * clear the synthetic-value cache and notify subscribers. Idempotent — a no-op
 * when the value is unchanged.
 * @param {boolean} enabled desired state.
 * @returns {Promise<boolean>} the resolved state.
 */
export async function setDemoMode(enabled) {
    const next = enabled === true;
    if (next === demoOn) {
        return next;
    }

    demoOn = next;

    // Reset the memoised fake values so a fresh session doesn't inherit the
    // previous run's cache (outputs stay deterministic either way). Loaded lazily
    // so that merely reading the demo flag (from the core role/menu helpers) never
    // pulls in the heavy faker chain — keeps `isDemoMode()` importable anywhere.
    try {
        // eslint-disable-next-line global-require
        require('./demoFaker').clearFakeCache();
    } catch (_) {
        // demoFaker not ready yet — harmless
    }

    notify();

    return next;
}

/**
 * Initialise the flag at boot. Demo mode is session-only and NEVER persisted, so
 * this purges any value left behind by older builds (AsyncStorage + its
 * SecureStore/Keychain twin) so it can never survive a cold start, logout, or
 * reinstall. Must run once at boot (App.jsx) BEFORE the first data fetch.
 *
 * It deliberately does NOT force `demoOn = false`: on a genuine cold start the
 * module singleton is freshly loaded so `demoOn` is already false, but switching
 * the top-level app mode triggers `restartApp` (an in-process remount that keeps
 * JS module singletons), and a presenter switching Sales↔Service↔Management mid-
 * demo must not lose demo mode. Preserving the in-memory value keeps the switch
 * alive across a restart while still being OFF on a true cold start; logout and
 * the Settings switch reset it explicitly via `setDemoMode(false)`.
 * @returns {Promise<boolean>} the hydrated state (in-memory value, false on cold start).
 */
export async function hydrateDemoMode() {
    // Best-effort purge of any legacy PERSISTED flag; never block boot on it.
    // (The in-memory `demoOn` is intentionally preserved — see the docblock.)
    try {
        await removeSetting(DEMO_MODE_STORAGE_KEY);
    } catch (_) {
        // storage unavailable — the in-memory default (off) still applies
    }

    notify();

    return demoOn;
}
